import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Search, ChevronRight, AlertTriangle, Sparkles, Settings, Download,
    Filter, RefreshCw, FileText, Receipt, ArrowRight, Clock,
} from "lucide-react";
import { resolveBillStatus } from "@/lib/statusResolvers";
import { usePermission } from "@/lib/usePermission";
import TollBillDetailModal from "@/components/TollBillDetailModal";
import TollGenerateInvoicesModal from "@/components/TollGenerateInvoicesModal";

const STATUS_FILTERS = [
    { value: "",         label: "All" },
    { value: "matched",  label: "Matched" },
    { value: "unmatched",label: "Unmatched" },
    { value: "disputed", label: "Disputed" },
    { value: "invoiced", label: "Invoiced" },
    { value: "void",     label: "Void" },
    { value: "new",      label: "New" },
];

const DISPUTE_FILTERS = [
    { value: "", label: "All" },
    { value: "true", label: "Disputed only" },
    { value: "false", label: "Non-disputed" },
];

const LIMIT_OPTIONS = [25, 50, 100];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) { if (!s) return "—"; return String(s).slice(0, 10); }

const _isUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(s || "").trim());

/**
 * Phase TOLLBILL-P5c (2026-07-10) — Primary Bills landing page.
 *
 * Bills are the first-class entity as of P5a. This page becomes the
 * primary Tolls entry point (TopNav /admin/tolls/bills). The legacy
 * transactions page at /admin/tolls remains available as a secondary
 * drill-down surface.
 *
 * Backend: GET /api/admin/tolls/bills (filters: customer_id, rental_id,
 * plate, authority, status, dispute_flag, date_from, date_to, q, limit,
 * skip). Extended q searches plate + authority + bill_id + document_type
 * + owner + customer.company_name + rental.unit_number.
 */
export default function AdminTollsBillsListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get("status") || "";
    const disputeFilter = searchParams.get("dispute") || "";
    const dateFrom = searchParams.get("from") || "";
    const dateTo = searchParams.get("to") || "";
    const customerId = searchParams.get("customer_id") || "";
    const rentalId = searchParams.get("rental_id") || "";
    const plateFilter = searchParams.get("plate") || "";
    const authorityFilter = searchParams.get("authority") || "";
    const limit = Number(searchParams.get("limit") || 50);
    const skip = Number(searchParams.get("skip") || 0);
    const [q, setQ] = useState(searchParams.get("q") || "");

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const [detailBillId, setDetailBillId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [genOpen, setGenOpen] = useState(false);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");
    const canManage = hasPermission("Tolls", "Manage");
    const canInvoice = hasPermission("Tolls", "Invoice");

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, String(value));
        if (key !== "skip" && key !== "limit") next.delete("skip");
        setSearchParams(next, { replace: true });
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit, skip };
            if (status) params.status = status;
            if (disputeFilter !== "") params.dispute_flag = disputeFilter === "true";
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (plateFilter) params.plate = plateFilter.trim();
            if (authorityFilter) params.authority = authorityFilter.trim();
            // Customer/Rental filter inputs accept BOTH a UUID and a
            // free-text name/unit — fold non-UUID into `q` per the
            // Phase 4c pattern.
            const _qExtras = [];
            if (customerId) {
                if (_isUuid(customerId)) params.customer_id = customerId;
                else _qExtras.push(customerId.trim());
            }
            if (rentalId) {
                if (_isUuid(rentalId)) params.rental_id = rentalId;
                else _qExtras.push(rentalId.trim());
            }
            const qStr = [(searchParams.get("q") || "").trim(), ..._qExtras]
                .filter(Boolean).join(" ").trim();
            if (qStr) params.q = qStr;
            const { data } = await api.get("/admin/tolls/bills", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status, disputeFilter, dateFrom, dateTo, customerId, rentalId, plateFilter, authorityFilter, limit, skip, searchParams.get("q")]);

    const onSearch = (e) => {
        e.preventDefault();
        updateParam("q", q.trim());
    };

    const openDetail = (bill) => {
        setDetailBillId(bill.bill_id);
        setDetailOpen(true);
    };

    const quickMarkDisputed = async (bill) => {
        const reason = window.prompt(`Mark bill ${bill.bill_id} (and children) as disputed. Reason?`, "");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/mark-disputed`, { reason: reason.trim() });
            toast.success("Marked disputed — children cascaded");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const quickResolve = async (bill) => {
        const outcome = window.prompt(`Resolve dispute on ${bill.bill_id}. Type "billable" or "voided":`, "billable");
        if (!outcome) return;
        const clean = outcome.trim().toLowerCase();
        if (clean !== "billable" && clean !== "voided") { toast.error("Outcome must be billable or voided"); return; }
        try {
            await api.post(`/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/resolve-dispute`, { outcome: clean });
            toast.success(`Dispute resolved: ${clean}`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const exportCsv = () => {
        if (!items.length) { toast.info("Nothing to export"); return; }
        const cols = [
            "bill_id","document_type","authority","plate","plate_state","bill_date","due_date",
            "customer_name","rental_unit_number","toll_amount_cents","processing_fee_cents",
            "late_fees_cents","previous_balance_cents","total_due_cents","status","dispute_flag","invoice_id",
        ];
        const lines = [cols.join(",")];
        for (const b of items) {
            const row = cols.map((c) => {
                const raw = b[c];
                if (raw == null) return "";
                const s = String(raw).replace(/"/g, '""');
                return `"${s}"`;
            });
            lines.push(row.join(","));
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `toll-bills-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (limit || 1))), [total, limit]);
    const currentPage = useMemo(() => Math.floor(skip / (limit || 1)) + 1, [skip, limit]);

    if (permReady && !canView) {
        return (
            <DashboardShell>
                <Card className="mt-8">
                    <CardContent className="p-10 text-center">
                        <AlertTriangle size={28} className="mx-auto text-amber-500" />
                        <h2 className="mt-3 font-display text-xl font-semibold" data-testid="bills-access-denied">Access denied</h2>
                        <p className="mt-1 text-sm text-slate-600">You do not have permission to view toll bills.</p>
                    </CardContent>
                </Card>
            </DashboardShell>
        );
    }

    const feesSum = (b) => (b.processing_fee_cents || 0) + (b.late_fees_cents || 0) + (b.previous_balance_cents || 0);

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Ancillary billing</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                        <Receipt size={26} className="text-blue-900" /> Toll bills
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Toll bills ingested from TollBill (with PDFs + fee breakdown + child transactions), matched to rentals, and rolled into draft invoices with markup.</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                        <p className="text-3xl font-display font-bold text-slate-900 tabular-nums" data-testid="bills-total-count">{total}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Bills</p>
                    </div>
                    <Button asChild variant="ghost" className="gap-1.5 text-slate-600" data-testid="bills-view-transactions">
                        <Link to="/admin/tolls">
                            View individual transactions <ArrowRight size={14} />
                        </Link>
                    </Button>
                    <Button variant="outline" onClick={exportCsv} className="gap-1.5" data-testid="bills-export-csv">
                        <Download size={14} /> Export CSV
                    </Button>
                    <Button asChild variant="outline" className="gap-1.5" data-testid="bills-settings-link">
                        <Link to="/admin/tolls/settings"><Settings size={14} /> Settings</Link>
                    </Button>
                    {canInvoice && (
                        <Button
                            onClick={() => setGenOpen(true)}
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="bills-generate-btn"
                        >
                            <Sparkles size={14} /> Generate draft invoices
                        </Button>
                    )}
                </div>
            </div>

            {/* Status chip filters */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="bills-status-chips">
                {STATUS_FILTERS.map((opt) => {
                    const active = status === opt.value;
                    return (
                        <button
                            key={opt.value || "all"}
                            onClick={() => updateParam("status", opt.value)}
                            data-testid={`bills-chip-${opt.value || "all"}`}
                            className={
                                "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                                (active
                                    ? "bg-blue-900 text-white"
                                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50")
                            }
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>

            {/* Filter card */}
            <Card className="mt-4">
                <CardContent className="p-4 flex flex-wrap gap-3 items-center">
                    <form onSubmit={onSearch} className="flex-1 min-w-[220px] flex gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search bill_id, plate, authority, owner, customer, unit…"
                                className="pl-9"
                                data-testid="bills-search-input"
                            />
                        </div>
                        <Button type="submit" variant="outline" data-testid="bills-search-go">Search</Button>
                    </form>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <span>From</span>
                        <Input type="date" value={dateFrom} onChange={(e) => updateParam("from", e.target.value)}
                               className="w-[160px]" data-testid="bills-from-date" />
                        <span>to</span>
                        <Input type="date" value={dateTo} onChange={(e) => updateParam("to", e.target.value)}
                               className="w-[160px]" data-testid="bills-to-date" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Filter size={14} className="text-slate-400" />
                        <select
                            value={disputeFilter}
                            onChange={(e) => updateParam("dispute", e.target.value)}
                            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="bills-dispute-filter"
                        >
                            {DISPUTE_FILTERS.map((o) => (
                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={customerId}
                            onChange={(e) => updateParam("customer_id", e.target.value)}
                            placeholder='Customer — "MRSM Express" or ID'
                            className="w-[220px] text-xs"
                            data-testid="bills-customer-filter"
                        />
                        <Input
                            value={rentalId}
                            onChange={(e) => updateParam("rental_id", e.target.value)}
                            placeholder='Rental — "TRK-1023" or ID'
                            className="w-[200px] text-xs"
                            data-testid="bills-rental-filter"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={plateFilter}
                            onChange={(e) => updateParam("plate", e.target.value)}
                            placeholder="Plate (exact)"
                            className="w-[140px] text-xs"
                            data-testid="bills-plate-filter"
                        />
                        <Input
                            value={authorityFilter}
                            onChange={(e) => updateParam("authority", e.target.value)}
                            placeholder="Authority (exact)"
                            className="w-[170px] text-xs"
                            data-testid="bills-authority-filter"
                        />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <select
                            value={limit}
                            onChange={(e) => updateParam("limit", e.target.value)}
                            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="bills-page-size"
                        >
                            {LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                        <Button variant="ghost" size="sm" onClick={load} data-testid="bills-refresh" title="Refresh">
                            <RefreshCw size={14} />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="bills-loading">Loading…</p>}
                    {!loading && items.length === 0 && (
                        <div className="p-12 text-center" data-testid="bills-empty">
                            <p className="text-sm text-slate-600">No toll bills match your filters.</p>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm table-fixed">
                                <colgroup>
                                    <col className="w-[86px]" />   {/* Bill date */}
                                    <col className="w-[86px]" />   {/* Due */}
                                    <col className="w-[200px]" />  {/* Authority (ellipsis) */}
                                    <col />                         {/* Customer — flex */}
                                    <col className="w-[86px]" />   {/* Rental */}
                                    <col className="w-[80px]" />   {/* Tolls */}
                                    <col className="w-[80px]" />   {/* Fees */}
                                    <col className="w-[86px]" />   {/* Total */}
                                    <col className="w-[104px]" />  {/* Status + inline badges */}
                                    <col className="w-[52px]" />   {/* Actions (Dispute icon) */}
                                </colgroup>
                                <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                    <tr>
                                        <th className="text-left py-3 px-2">Bill date</th>
                                        <th className="text-left py-3 px-2">Due</th>
                                        <th className="text-left py-3 px-2">Authority</th>
                                        <th className="text-left py-3 px-2">Customer</th>
                                        <th className="text-left py-3 px-2">Rental</th>
                                        <th className="text-right py-3 px-2">Tolls</th>
                                        <th className="text-right py-3 px-2">Fees</th>
                                        <th className="text-right py-3 px-2">Total</th>
                                        <th className="text-left py-3 px-2">Status</th>
                                        <th className="text-right py-3 px-2 sr-only">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {items.map((b) => {
                                        const st = resolveBillStatus(b);
                                        const feeTotal = feesSum(b);
                                        const feeTooltip = `Processing: ${fmtMoney(b.processing_fee_cents || 0)} · Late: ${fmtMoney(b.late_fees_cents || 0)} · Previous: ${fmtMoney(b.previous_balance_cents || 0)}`;
                                        return (
                                            <tr
                                                key={b.bill_id}
                                                data-testid={`bills-row-${b.bill_id}`}
                                                onClick={() => openDetail(b)}
                                                className="hover:bg-slate-50/50 cursor-pointer"
                                                title={`Bill ${b.bill_id}`}
                                            >
                                                <td className="py-3 px-2 text-slate-700 whitespace-nowrap">{fmtDate(b.bill_date)}</td>
                                                <td className="py-3 px-2 whitespace-nowrap">
                                                    <span className={st.isOverdue ? "text-red-700 font-semibold inline-flex items-center gap-1" : "text-slate-700"}>
                                                        {st.isOverdue && <Clock size={11} data-testid={`bills-overdue-${b.bill_id}`} />}
                                                        {fmtDate(b.due_date)}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2 overflow-hidden">
                                                    <div className="text-slate-800 truncate" title={b.authority || ""}>{b.authority || "—"}</div>
                                                    {b.document_type && (
                                                        <div className="text-[10.5px] text-slate-500 mt-0.5 truncate" title={b.document_type}>{b.document_type}</div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 overflow-hidden">
                                                    {b.customer_id ? (
                                                        <Link
                                                            to={`/admin/customers/${b.customer_id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-blue-900 hover:underline text-xs truncate block"
                                                            title={b.customer_name || ""}
                                                            data-testid={`bills-customer-${b.bill_id}`}
                                                        >
                                                            {b.customer_name || `${b.customer_id.slice(0, 8)}…`}
                                                        </Link>
                                                    ) : <span className="text-slate-400 text-xs">—</span>}
                                                </td>
                                                <td className="py-3 px-2 overflow-hidden">
                                                    {b.rental_id ? (
                                                        <Link
                                                            to={`/admin/rentals/${b.rental_id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-blue-900 hover:underline text-xs font-mono truncate block"
                                                            title={b.rental_unit_number || ""}
                                                            data-testid={`bills-rental-${b.bill_id}`}
                                                        >
                                                            {b.rental_unit_number || `${b.rental_id.slice(0, 8)}…`}
                                                        </Link>
                                                    ) : <span className="text-slate-400 text-xs">—</span>}
                                                </td>
                                                <td className="py-3 px-2 text-right tabular-nums text-slate-800 whitespace-nowrap">{fmtMoney(b.toll_amount_cents)}</td>
                                                <td className="py-3 px-2 text-right tabular-nums text-slate-600 whitespace-nowrap" title={feeTooltip} data-testid={`bills-fees-${b.bill_id}`}>
                                                    {feeTotal > 0 ? fmtMoney(feeTotal) : <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="py-3 px-2 text-right font-semibold tabular-nums whitespace-nowrap" data-testid={`bills-total-${b.bill_id}`}>
                                                    {fmtMoney(b.total_due_cents)}
                                                </td>
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-1 flex-nowrap">
                                                        <span
                                                            className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${st.tone}`}
                                                            data-testid={`bills-status-${b.bill_id}`}
                                                        >
                                                            {st.label}
                                                        </span>
                                                        {b.has_attachment && (
                                                            <FileText size={12} className="text-slate-500 shrink-0" title="PDF attachment available" data-testid={`bills-attach-${b.bill_id}`} />
                                                        )}
                                                        {b.dispute_flag && st.code !== "disputed" && (
                                                            <AlertTriangle size={12} className="text-red-600 shrink-0" title="Dispute flag set" />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                                                    {canManage && !st.isTerminal && !st.isDisputed && (
                                                        <button
                                                            onClick={() => quickMarkDisputed(b)}
                                                            data-testid={`bills-dispute-${b.bill_id}`}
                                                            title="Mark disputed"
                                                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-red-700 hover:bg-red-50 transition-colors"
                                                        >
                                                            <AlertTriangle size={14} />
                                                        </button>
                                                    )}
                                                    {canManage && st.isDisputed && (
                                                        <button
                                                            onClick={() => quickResolve(b)}
                                                            data-testid={`bills-resolve-${b.bill_id}`}
                                                            title="Resolve dispute"
                                                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-700 hover:bg-slate-100 transition-colors"
                                                        >
                                                            <ChevronRight size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {!loading && total > limit && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-600" data-testid="bills-pagination">
                    <span>Page {currentPage} of {totalPages} · {total} total</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={skip <= 0} onClick={() => updateParam("skip", Math.max(0, skip - limit))} data-testid="bills-page-prev">Previous</Button>
                        <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => updateParam("skip", skip + limit)} data-testid="bills-page-next">Next</Button>
                    </div>
                </div>
            )}

            <TollBillDetailModal
                open={detailOpen}
                onOpenChange={setDetailOpen}
                billId={detailBillId}
                canManage={canManage}
                onChanged={load}
            />
            <TollGenerateInvoicesModal
                open={genOpen}
                onOpenChange={setGenOpen}
                onCreated={load}
            />
        </DashboardShell>
    );
}
